import { Divider, styled } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "src/components/common/Breadcrumb/Breadcrumb";
import TableFilter from "src/components/common/TableFilter/TableFilter";
import useRolePermission from "src/hooks/useRolePermission";
import { useEffect, useRef, useState } from "react";
import { ACTION } from "src/util/utils";
import { Modal, Spin } from "antd";
import Button from "src/components/common/Button/Button";
import GenericGrid from "src/components/common/Grid/GenericGrid";
import filterColumns from "./filterColumns";
import { InventoryFinalUpdateUploadModal } from "./InventoryUpdateUploadModal";
import { cleanImportLayout, fetchInventoryFinalUpdateSummary, MODULE_KEY } from "./actions";
import { showErrorNotification, showSuccessNotification } from "src/util/notifications";
import { useMsal } from "@azure/msal-react";
import { finalUpdate, runInventoryFinalUpdateValidation } from "./saga";
import { usePDF } from "react-to-pdf";
import { UploadOutlined } from "@ant-design/icons";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import * as cheerio from 'cheerio';

export const InventoryUpdate = ({
  onCancel,
  importFile,
  params,
}: any) => {
  const dispatch = useDispatch();
  const { accounts }: any = useMsal();
  const { hasPermission } = useRolePermission();
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [visibleRunValidation, setVisibleRunValidation] = useState(false);
  const [runValidationForSummaryId, setRunValidationForSummaryId] = useState<string>('');
  const [visibleFinalUpdate, setVisibleFinalUpdate] = useState(false);
  const [finalUpdateForSummaryId, setFinalUpdateForSummaryId] = useState<string>('');
  const [data, setData] = useState<string>('');
  const [showPopup, setShowPopup] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const hasMounted = useRef(false);
  const stateKey = `${MODULE_KEY}${ACTION.STATE_KEY}`;
  const { toPDF, targetRef } = usePDF({ filename: "ValidationReport.pdf" });
  let importData = useSelector(
    (state: any) => state[stateKey]
  ).importValidationData.ValidationResult;


  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf',
        fontWeight: 'normal',
      },
      {
        src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc9.ttf',
        fontWeight: 'bold',
      }
    ]
  });
  const COLUMN_COUNT = 7;
  const COLUMN_WIDTH = `${100 / COLUMN_COUNT}%`;
  const BORDER_STYLE = {
    borderColor: 'solid black',
    borderWidth: 0.5,
  };
  const styles = StyleSheet.create({
    page: {
      padding: 20,
      fontSize: 10,
      fontFamily: 'Roboto',
    },
    sectionTitle: {
      fontSize: 10,
      marginBottom: 10,
      fontWeight: '500px',
    },
    table: {
      width: '100%',
      marginBottom: 15,
      ...BORDER_STYLE,
    },
    tableRow: {
      flexDirection: 'row',
      ...BORDER_STYLE,
    },
    tableHeaderCol: {
      ...BORDER_STYLE,
      backgroundColor: '#f2f2f2',
      padding: 8,
      textAlign: 'center',
      flex: 1,
    },
    tableCol: {
      ...BORDER_STYLE,
      padding: 4,
      textAlign: 'center',
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
    },
    tableHeaderCell: {
      fontSize: 10,
      fontWeight: 'bold',
    },
    tableCell: {
      fontSize: 10,
      fontWeight: '300px'
    },
  });

  const parseHtmlToTables = (htmlString: string) => {
    const $ = cheerio.load(htmlString);
    const tables: { title: string; rows: string[][] }[] = [];

    $('h6').each((_, h6: any) => {
      const title = $(h6).text();
      const rows: string[][] = [];
      const table = $(h6).next('table');

      let columnCount = 0;

      table.find('tr').each((_, tr: any) => {
        const row: string[] = [];
        $(tr).find('th, td').each((_, td: any) => {
          row.push($(td).text().trim());
        });
        if (row.length > columnCount) columnCount = row.length;
        rows.push(row);
      });

      const normalizedRows = rows.map(row =>
        row.length < columnCount ? [...row, ...Array(columnCount - row.length).fill('')] : row
      );

      tables.push({ title, rows: normalizedRows });
    });

    return tables;
  };

  const ValidationReportPDF = ({ tables }: { tables: { title: string; rows: string[][] }[] }) => (
    <Document>
      <Page size="A4" style={styles.page}>
        {tables.map((table, idx) => {
          const colCount = table.rows[0]?.length || 1;
          return (
            <View key={idx}>
              <Text style={styles.sectionTitle}>{table.title}</Text>
              <View style={styles.table}>
                {table.rows.map((row, rowIdx) => (
                  <View key={rowIdx} style={styles.tableRow}>
                  {row.length === 1 ? (
                    <View
                      style={{
                        ...styles.tableCol,
                        flex: colCount, 
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        style={{
                          ...styles.tableCell,
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                      {row[0]}
                      </Text>
                    </View>
                  ) : (
                    row.map((cell, colIdx) => (
                      <View
                        key={colIdx}
                        style={rowIdx === 0 ? styles.tableHeaderCol : styles.tableCol}
                      >
                        <Text
                          style={{
                            ...(rowIdx === 0 ? styles.tableHeaderCell : styles.tableCell),
                            fontSize: cell.length > 10 ? 8 : 10,
                          }}
                        >
                          {cell}
                        </Text>
                      </View>
                    ))
                  )}
                </View>
                ))}
              </View>
            </View>
          );
        })}
      </Page>
    </Document>
  );

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchInventoryFinalUpdateSummary({}))
  }, []);

  const runValidationModal = (record: any) => {
    setVisibleRunValidation(true);
    setRunValidationForSummaryId(record);
  };

  const finalUpdateModal = (record: any) => {
    setVisibleFinalUpdate(true);
    setFinalUpdateForSummaryId(record);
    setRunValidationForSummaryId(record);
  }

  const handleCancel = () => {
    dispatch(fetchInventoryFinalUpdateSummary({}));
  };

  let filterValues = filterColumns(runValidationModal, finalUpdateModal);

  useEffect(() => {
    try {
      if (typeof importData === "string") {
        dispatch(fetchInventoryFinalUpdateSummary({}));
        handleCancel();
        setConfirmLoading(false);
        setData(importData);
        setShowPopup(true);
      } else if (Object.keys(importData)?.length) {
        setErrorModal(true);
        dispatch(fetchInventoryFinalUpdateSummary({}));
      }
    } catch (error: any) {
      if (error && error?.response) {
        if (error?.response?.data) {
          showErrorNotification(error.response.data.error.messages[0]);
        } else {
          showErrorNotification(error.response);
        }
      }
    }
  }, [importData]);

  const handleApiCall = async (apiFunction: Function, payload: any, onSuccess?: Function) => {
    try {
      setConfirmLoading(true);
      const res = await apiFunction(payload);
      if (onSuccess) onSuccess(res);
    } catch (error: any) {
      showErrorNotification(error?.response?.data?.error?.messages?.[0] || "An error occurred");
    } finally {
      setConfirmLoading(false);
    }
  };

  const runValidation = async (summaryId: string, createdBy: string) => {
    const payload = {
      InventoryFinalUpdateSummaryId: summaryId,
      CreatedBy: createdBy,
    };
    await handleApiCall(runInventoryFinalUpdateValidation, payload, (res: any) => {
      setData(res?.ValidationResult);
      setShowPopup(true);
    });
    setVisibleRunValidation(false);
    dispatch(fetchInventoryFinalUpdateSummary({}));
  };

  const handleRunValidation = async () => {
    if (!runValidationForSummaryId || !accounts?.[0]?.username) return;
    await runValidation(runValidationForSummaryId, accounts[0].username);
  };

  const handleValidationResultClose = () => {
    dispatch(cleanImportLayout());
    setShowPopup(false);
  };
  const handleFinalUpdate = async () => {
    if (!finalUpdateForSummaryId || !accounts?.[0]?.username) return;

    const payload = {
      InventoryFinalUpdateSummaryId: finalUpdateForSummaryId,
      FinalUpdateBy: accounts[0].username,
    };

    await handleApiCall(finalUpdate, payload, async (res: any) => {
      await dispatch(fetchInventoryFinalUpdateSummary({}));
      if (res.ValidationMessage.toLowerCase() === 'success') {
        showSuccessNotification(`${res.TotalLocations} records are added in the system`);
      }
      else {
        showErrorNotification(`${res.ValidationMessage}`);
      }
    });
    setVisibleFinalUpdate(false);
  };
  return (
    <>
      <Spin spinning={confirmLoading}>
        <Divider />
        <Breadcrumb>
          <span>Inventory Final Update</span>
        </Breadcrumb>
        <TableFilter>
          <div className="form align-items-end flex"></div>
          <div className="d-flex justify-content-end gap-2">
            {hasPermission("finalinventoryupdate_import") && (
              <Button
                size="middle"
                icon={<UploadOutlined />}
                ant-click-animating-without-extra-node="true"
                onClick={() => setOpenUpload(true)}
              >
                {" "}
                Upload Inventory Counts
              </Button>
            )}
          </div>
        </TableFilter>
        <GenericGrid
          columns={filterValues.columns}
          data={filterValues.data}
          enableRowSelection={false}
          rowSelection={[]}
          columnVisibility={{}}
          columnPinning={{}}
          enableColumnOrdering={false}
          filterDisplayMode="popover"
          highlightFilterMatch={true}
          paginationDisplayMode="pages"
          paginationProps={{
            color: 'primary',
            shape: 'rounded',
            showRowsPerPage: true,
            variant: 'outlined',
            rowsPerPageOptions: [10, 50, 100, 500, 1000]
          }}
          enableStickyHeader={true}
          enableStickyFooter={true}
          enableColumnPinning={true}
          isLoading={filterValues.loading}
          muiTableBodyCellProps={{
            sx: {
              fontSize: '11px',
              textAlign: 'center',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              padding: '2px 10px'
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: '10px',
              fontWeight: '800',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              textAlign: 'center',
              padding: '2px 10px'
            }
          }}
          muiTableBodyRowProps={{
            sx: {
              "& td": {
                padding: "2px 10px",
              },
              textAlign: 'center',
            },
          }}
        >
        </GenericGrid>
        <InventoryFinalUpdateUploadModal
          visible={openUpload}
          onCancel={() => setOpenUpload(false)}
          stateKey={stateKey}
          importFile={importFile}
          confirmLoading={confirmLoading}
          setConfirmLoading={setConfirmLoading}
        />
        <Modal
          title="Validation Confirmation"
          okText="Ok"
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          open={visibleRunValidation}
          onOk={handleRunValidation}
          onCancel={() => setVisibleRunValidation(false)}
        >
          <span>
            Are you sure you wish to run validation?
          </span>
        </Modal>
        <Modal
          title="Final Update Confirmation"
          okText="Ok"
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          open={visibleFinalUpdate}
          onOk={handleFinalUpdate}
          onCancel={() => setVisibleFinalUpdate(false)}
        >
          <span>
            Are you sure you wish to run final update?
          </span>
        </Modal>
        <Modal
          title="Validation Result"
          closable={false}
          okText="Ok"
          onOk={handleValidationResultClose}
          width={1000}
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          footer={
            <div className="d-flex justify-content-end mb-2">
              <PDFDownloadLink
                document={<ValidationReportPDF tables={parseHtmlToTables(data)} />}
                fileName="ValidationReport.pdf"
              >
                {({ loading }) =>
                  loading ? (
                    <Button
                      type="primary"
                      className="ghost-button"
                      disabled>
                      PDF
                    </Button>
                  ) : (
                    <Button type="primary"
                      className="ghost-button">
                      PDF
                    </Button>
                  )
                }
              </PDFDownloadLink>
              <Button
                type="primary"
                className="ghost-button"
                style={{ marginLeft: "5px" }}
                onClick={handleValidationResultClose}
              >
                Ok
              </Button>
            </div>
          }
          open={showPopup}
          onCancel={handleValidationResultClose}>
          <VRContainer ref={targetRef} className="pdf-content"
            style={{ padding: 20 }}>
            <div dangerouslySetInnerHTML={{ __html: data as string }} />
          </VRContainer>
        </Modal>
      </Spin>
    </>
  );

}
const VRContainer = styled('div')`
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th, td {
    border: 1px solid black;
    padding: 8px; /* Increased padding for better readability */
    text-align: center;
  }
  th {
    background-color: #f2f2f2;
    font-weight: bold; /* Added font weight for better visibility */
  }
`;
